import React, { memo, useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import { useHistory } from 'react-router-dom';
import 'swiper/css/swiper.css';
import styles from './AIGC.module.less';

import { CONTEXT } from '../../config';
import { logging } from '../../actions/log';
import {
  aigcGetReplys,
  aigcShowDialog,
  aigcReplyIsBusing,
  aigcShowMessage,
} from '../../actions/aigc';
import FirstConversation from './components/FirstConversation';
import UserQuestion from './components/UserQuestion';
import AIGCHeader from './components/AIGCHeader/AIGCHeader';
import AIGCOpt from './components/AIGCOpt/AIGCOpt';
import AiAnswerIndex from './components/AiAnswer/AiAnswerIndex';
import AIGCAction from './AIGCAction';
import { addClass, changeClass, getStringCharLength } from '../../utils/utils';
import useResizeObserver from '../../utils/hooks/useResizeObserver';
import { PCStoreJsBridge } from '../../nativeapi';
import AiAnswerLoading from './components/AiAnswer/AiAnswerLoading';
import AIGCMessage from './components/AIGCMessage/AIGCMessage';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectCommandLine = createSelector(
  state => state.native,
  native => native.commandLine,
);

const selectAIGC = createSelector(
  state => state.aigc,
  aigc => aigc,
);

export default memo(() => {
  const AIGCRef = useRef(null);
  const contentRef = useRef(null);
  const aigcBoxRef = useRef(null);
  const inputRef = useRef(null);

  // 监听 展示区域大小变化
  const dimensions = useResizeObserver(aigcBoxRef);

  const history = useHistory();
  const dispatch = useDispatch();
  const logInfoData = useSelector(selectLogInfo);
  const AIGCObj = useSelector(selectAIGC); //
  const commandLine = useSelector(selectCommandLine);
  const [inputValue, setInputValue] = useState('');
  const [inputFocus, setInputFocus] = useState(false);
  const [commandLineOld, setCommandLine] = useState('');

  useEffect(() => {
    dispatch(
      logging({
        category: 'P',
        action: `Search_Aigc`,
        label: '',
        param2: {
          source: `AIGC_${logInfoData.source}`,
        },
      }),
    );
  }, []);
  // 一进入聊天窗口就滚动到底部
  useEffect(() => {
    if (contentRef && contentRef.current && contentRef.current.scrollHeight) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [contentRef]);
  useEffect(() => {
    if (commandLineOld === '') {
      commandLine && setCommandLine(commandLine);
    }
    if (commandLineOld && commandLine && commandLine !== commandLineOld) {
      commandLine && setCommandLine(commandLine);
      handleClose();
    }
  }, [commandLine, commandLineOld]);

  // 如果搜索页面进来的就自动填入搜索词
  useEffect(() => {
    if (
      logInfoData &&
      logInfoData?.search_word &&
      history?.location?.pathname &&
      history?.location?.pathname?.indexOf('searchresult') > -1
    ) {
      setInputValue(value => {
        if (value.trim() === '') {
          return logInfoData?.search_word;
        }
        return value;
      });
    }
  }, []);

  // 控制aigc在最下一行
  useEffect(() => {
    // 只在回答--打字过程中，才控制滚动在最低端
    if ([0, 4, 5].indexOf(AIGCObj.isBusing) !== -1) {
      return;
    }
    if (contentRef && contentRef.current && contentRef.current.scrollHeight) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [dimensions, contentRef, AIGCObj?.isBusing]);

  const clearInputAll = useCallback(() => {
    setTimeout(() => {
      setInputValue('');
      setInputFocus(false);
      if (inputRef?.current && inputRef?.current?.style) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = '19px';
      }
    }, 200);
  }, []);

  const handleChange = useCallback(ev => {
    ev.persist();
    let inputDom = ev.target;
    let inputValue = inputDom.value;

    if (inputDom) {
      inputDom.style.height = 'auto';
      inputDom.style.height = inputDom.scrollHeight + 'px';
      if (inputDom.scrollHeight >= 55) {
        inputDom.style.height = '57px';
        inputDom.style['overflow-y'] = 'auto';
      }
    }

    if (inputValue.length > 100) {
      setInputValue(inputValue.splice(0, 100));
    } else {
      setInputValue(inputValue);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (!inputValue.trim()) {
      setInputValue('');
      if (inputRef?.current && inputRef?.current?.style) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = '19px';
      }
      return;
    }
    // 清空输入框
    clearInputAll();
    dispatch(aigcReplyIsBusing({ isBusing: 1 }));
    dispatch(aigcGetReplys({ userText: inputValue }));

    // 打桩
    dispatch(
      logging({
        category: 'C',
        action: 'Search_Aigc_Input',
        label: '',
        param2: {
          source: `AIGC_${logInfoData.source}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          search_word: inputValue,
          type: 1,
        },
      }),
    );
  }, [logInfoData, inputValue, inputRef, AIGCObj]);

  const handleKeyDown = useCallback(
    ev => {
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault();
        if ([0, 4, 5].indexOf(AIGCObj.isBusing) === -1) {
          dispatch(aigcShowMessage({ isShow: true, content: `小乐正在思考中，请耐心等待...` }));
          return;
        }
        if (inputValue) {
          handleSubmit();
        }
        return;
      }
    },
    [inputValue, AIGCObj],
  );
  const handleFocus = useCallback(() => {
    // if ([0, 4, 5].indexOf(AIGCObj.isBusing) > -1) {
    // }
    setInputFocus(true);
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  }, [inputRef, AIGCObj]);

  const handleBlur = useCallback(() => {
    setInputFocus(false);
  }, []);

  const handleClose = useCallback(() => {
    if (AIGCRef?.current) {
      changeClass(AIGCRef?.current, styles.aigc_out, styles.aigc_in);
    }
    setTimeout(() => {
      dispatch(
        aigcShowDialog({
          show: false,
        }),
      );
    }, 300);
  }, [AIGCRef]);

  return (
    <div className={styles.aigc_bg}>
      <div className={`${styles.aigc_blank}`} onClick={handleClose}></div>
      <div className={`${styles.aigc_box} ${styles.aigc_in}`} ref={AIGCRef}>
        <div className={`aigc_left_bg`}></div>
        <div className={`aigc_cont`}>
          <AIGCHeader AIGCRef={AIGCRef}></AIGCHeader>

          <div className={`aigc_header_line`}>
            {AIGCObj?.aigcMessage?.isShow && <AIGCMessage />}
          </div>
          <div className={`aigc_content`} ref={contentRef}>
            <div className={`aigc_content_box`} ref={aigcBoxRef}>
              {/*首次打开的弹窗*/}
              {AIGCObj?.aigcReplyList?.length === 0 && <FirstConversation />}
              {AIGCObj?.aigcReplyList?.map((el, index) => {
                if (el.chatType === 'usertext') {
                  return <UserQuestion key={index} question={el?.userText} />;
                }
                if (el.chatType === 'reply') {
                  return (
                    <AiAnswerIndex
                      answerList={[...el?.replys]}
                      key={index}
                      showOperate={index + 1 === AIGCObj?.aigcReplyList?.length}
                      h5IDAll={el?.h5IDAll}
                      userText={el?.userQuestion?.userText}
                    />
                  );
                }
              })}
              {/*有回答的时候的loading*/}
              {AIGCObj?.aigcReplyList?.length > 0 && AIGCObj?.isBusing === 1 && <AiAnswerLoading />}
            </div>
          </div>
          <AIGCOpt></AIGCOpt>
          <div
            className={inputFocus ? 'aigc_inputbox aigc_inputbox_focus' : `aigc_inputbox`}
            onClick={handleFocus}
          >
            <textarea
              className={'aigc_input'}
              type="text"
              // disabled={[0, 4, 5].indexOf(AIGCObj.isBusing) === -1 ? 'disable' : ''}
              ref={inputRef}
              rows={1}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              value={inputValue}
              onFocus={handleFocus}
              onBlur={handleBlur}
              autoComplete="off"
              placeholder={'请输入您的问题，描述越清晰，结果越准确~'}
            ></textarea>
            <div className={`aigc_input_num_box`}>
              <div className={inputValue?.length >= 100 ? `aigc_input_num_red` : `aigc_input_num`}>
                <span>{inputValue.length}</span>
                <span className={'aigc_input_num_'}>/100</span>
              </div>
              {inputValue?.length >= 100 && (
                <div className={`aigc_input_info`}>字数超过限制，请缩减字数</div>
              )}
            </div>
            {/* 0: 正常状态,  1: 请求数据中  2: 请求完成  3: 打字中  4; 打字暂停   5: 打字完成 */}
            {[0, 4, 5].indexOf(AIGCObj.isBusing) === -1 && (
              <div className={`aigc_input_busing`}></div>
            )}
            {[0, 4, 5].indexOf(AIGCObj.isBusing) > -1 && (
              <div
                className={`iconfont ${
                  inputValue
                    ? 'icon-ai_dianjifasong aigc_input_submit'
                    : 'icon-ai_fasong aigc_input_submit_dis'
                }`}
                onClick={handleSubmit}
              ></div>
            )}
            {/* {[0, 4, 5].indexOf(AIGCObj.isBusing) > -1 && (
              <div
                className={`iconfont icon-ai_fasong ${
                  inputValue && inputFocus ? 'aigc_input_submit' : 'aigc_input_submit_dis'
                }`}
                onMouseEnter={e => {
                  if (inputValue?.length > 0) {
                    changeClass(e.target, 'icon-ai_dianjifasong', 'icon-ai_fasong');
                  }
                }}
                onMouseLeave={e => {
                  if (inputValue?.length > 0) {
                    changeClass(e.target, 'icon-ai_fasong', 'icon-ai_dianjifasong');
                  }
                }}
                onClick={handleSubmit}
              ></div>
            )} */}
          </div>
        </div>
        <AIGCAction />
      </div>
    </div>
  );
});
